import React, {useState, useEffect, useCallback, useMemo, useRef, memo} from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import MUIContainer from '@/components/MUI-Components/MUI-Container';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import DisplayImage from '../display-image';
import {colors, fontSize, lineHeight, spacing, overlay, transition, typography, borderRadius, shadows, zIndex} from '@/styles/tokens';
import Eyebrow from '@/components/Common/Eyebrow';

interface AwardsProps {
    eyebrow?: string;
    heading?: string;
    awardsList: any[];
    className?: string;
    sx?: any;
    headingSx?: any;
    isLightTheme?: boolean;
    sliding?: boolean;
}

// ─── Module-level sx constants ────────────────────────────────────────────────

const defaultHeadingSx = {
    ...typography.sectionHeadingLg,
    color: colors.text.inverse,
    textAlign: 'center',
    mb: spacing[40],
    lineHeight: lineHeight[56]
} as const;

const ITEM_WIDTH_DESKTOP = 210;
const ITEM_GAP = 32;
const TRANSITION_MS = 450;
const AUTOPLAY_DELAY = 3000;
const CARD_HEIGHT = 204;

const arrowBtnSx = (side: 'left' | 'right', isLight?: boolean) =>
    ({
        position: 'absolute',
        top: '50%',
        // md: inside the slider's 56px side padding; -52 pushed the arrow past the viewport edge
        // (horizontal page scroll on full-width containers).
        [side]: {xs: -8, md: 4},
        transform: 'translateY(-50%)',
        zIndex: zIndex.overlay,
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: spacing[44],
        height: spacing[44],
        borderRadius: borderRadius.full,
        border: isLight ? `2px solid ${colors.text.primary}` : `2px solid ${overlay.whiteDim}`,
        color: isLight ? colors.text.primary : colors.text.inverse,
        background: 'transparent',
        transition: `background ${transition.base}`,
        '&:hover': {
            bgcolor: isLight ? overlay.darkHover : overlay.whiteFaint
        }
    } as const);

// ─── Component ────────────────────────────────────────────────────────────────

const Awards = ({eyebrow, heading, awardsList = [], headingSx, isLightTheme, sliding = true}: AwardsProps) => {
    const total = awardsList.length;

    const segmentSize = useMemo(() => {
        if (total === 0) return 0;
        return Math.ceil(15 / total) * total;
    }, [total]);

    const tripled = useMemo(() => {
        if (total === 0) return [];
        const copies = segmentSize / total;
        const repeated = Array.from({length: copies}, () => awardsList).flat();
        return [...repeated, ...repeated, ...repeated];
    }, [awardsList, total, segmentSize]);

    const [index, setIndex] = useState(segmentSize);
    const [animated, setAnimated] = useState(true);

    const resetting = useRef(false);
    const autoplayRef = useRef<ReturnType<typeof setInterval> | null>(null);
    const containerRef = useRef<HTMLDivElement>(null);

    const [step, setStep] = useState(ITEM_WIDTH_DESKTOP + ITEM_GAP);

    useEffect(() => {
        const measure = () => {
            if (!containerRef.current) return;

            const cw = containerRef.current.offsetWidth;

            if (globalThis.innerWidth < 600) {
                // One full-width card per slide, plus the track gap that follows it.
                setStep(cw + ITEM_GAP);
            } else {
                setStep(ITEM_WIDTH_DESKTOP + ITEM_GAP);
            }
        };

        measure();

        globalThis.addEventListener('resize', measure);

        return () => globalThis.removeEventListener('resize', measure);
    }, []);

    const startAutoplay = useCallback(() => {
        if (autoplayRef.current) {
            clearInterval(autoplayRef.current);
        }

        autoplayRef.current = setInterval(() => {
            if (!resetting.current) {
                setIndex((prev) => prev + 1);
            }
        }, AUTOPLAY_DELAY);
    }, []);

    useEffect(() => {
        if (!sliding || total === 0) return undefined;

        startAutoplay();

        return () => {
            if (autoplayRef.current) {
                clearInterval(autoplayRef.current);
            }
        };
    }, [sliding, total, startAutoplay]);

    useEffect(() => {
        if (total === 0 || !sliding) return undefined;

        let timer: ReturnType<typeof setTimeout> | undefined;

        if (index >= segmentSize * 2) {
            resetting.current = true;

            const excess = index - segmentSize * 2;

            timer = setTimeout(() => {
                setAnimated(false);
                setIndex(segmentSize + excess);
            }, TRANSITION_MS + 20);
        } else if (index < segmentSize) {
            resetting.current = true;

            const deficit = segmentSize - index;

            timer = setTimeout(() => {
                setAnimated(false);
                setIndex(segmentSize * 2 - deficit);
            }, TRANSITION_MS + 20);
        }

        return () => {
            if (timer) clearTimeout(timer);
        };
    }, [index, total, sliding, segmentSize]);

    useEffect(() => {
        if (!animated) {
            let raf2: number;

            const raf1 = requestAnimationFrame(() => {
                raf2 = requestAnimationFrame(() => {
                    setAnimated(true);
                    resetting.current = false;
                });
            });

            return () => {
                cancelAnimationFrame(raf1);
                if (raf2) {
                    cancelAnimationFrame(raf2);
                }
            };
        }

        return undefined;
    }, [animated]);

    const handlePrev = useCallback(() => {
        if (resetting.current) return;

        setIndex((prev) => prev - 1);

        startAutoplay();
    }, [startAutoplay]);

    const handleNext = useCallback(() => {
        if (resetting.current) return;

        setIndex((prev) => prev + 1);

        startAutoplay();
    }, [startAutoplay]);

    if (total === 0) return null;

    const cardSx = {
        // `cqw` = the slider viewport's width; `%` would resolve against the max-content track.
        width: {xs: '100cqw', sm: ITEM_WIDTH_DESKTOP},
        minWidth: {xs: '100cqw', sm: ITEM_WIDTH_DESKTOP},
        height: CARD_HEIGHT,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: 0,
        bgcolor: isLightTheme ? colors.background.default : overlay.whiteSubtle,
        border: isLightTheme ? `1px solid ${colors.neutral.grayLight}` : `1px solid ${overlay.whiteTenth}`,
        borderRadius: borderRadius.md,
        boxShadow: isLightTheme ? shadows.lg : shadows.lg,
        textDecoration: 'none',
        overflow: 'hidden',
        flexShrink: 0
    } as const;

    const renderCard = (item: any, i: number) => {
        const imageSrc = item.src ?? item.image;

        return (
            <Box
                key={`${item?.title || 'award'}-${i}`}
                component={item.link ? 'a' : 'div'}
                href={item.link || undefined}
                target={item.link ? '_blank' : undefined}
                rel={item.link ? `${item.badge ? 'nofollow ' : ''}noopener noreferrer` : undefined}
                sx={{
                    ...cardSx,
                    cursor: item.link ? 'pointer' : 'default'
                }}
            >
                {item.badge ? (
                    // Badge artwork in the 160×165 box its embed iframe used (see plain-sections/Awards).
                    <span style={{width: '160px', height: '165px'}}>
                        {/* eslint-disable-next-line @next/next/no-img-element -- self-hosted SVG at its natural ratio */}
                        <img
                            src={item.badge}
                            alt={item.title || ''}
                            width={153}
                            height={165}
                            loading="lazy"
                            decoding="async"
                            style={{display: 'block', width: 'auto', height: '165px'}}
                        />
                    </span>
                ) : (
                    imageSrc && (
                        <Box sx={{position: 'relative', width: '80%', height: '70%'}}>
                            <DisplayImage src={imageSrc} alt={item.title || ''} fill style={{objectFit: 'contain'}} />
                        </Box>
                    )
                )}
            </Box>
        );
    };

    return (
        <Box>
            <MUIContainer>
                {eyebrow && <Eyebrow text={eyebrow} onDark={!isLightTheme} sx={{textAlign: 'center'}} />}
                <Typography
                    variant="h2"
                    component="h2"
                    sx={{
                        ...defaultHeadingSx,
                        ...headingSx
                    }}
                    className="font-headers"
                >
                    {heading || 'Awards & Accolades'}
                </Typography>

                {sliding ? (
                    <Box
                        sx={{
                            position: 'relative',
                            px: {xs: spacing[16], md: spacing[56]}
                        }}
                    >
                        <Box sx={arrowBtnSx('left', isLightTheme)} onClick={handlePrev} role="button" aria-label="Previous">
                            <ArrowForwardIcon
                                sx={{
                                    transform: 'rotate(180deg)',
                                    fontSize: fontSize[22]
                                }}
                            />
                        </Box>

                        <Box
                            ref={containerRef}
                            sx={{
                                overflow: 'hidden',
                                width: '100%',
                                containerType: 'inline-size'
                            }}
                        >
                            <Box
                                sx={{
                                    display: 'flex',
                                    width: 'max-content',
                                    minWidth: 0,
                                    flexDirection: 'row',
                                    flexWrap: 'nowrap',
                                    gap: spacing[32],
                                    transform: `translateX(-${index * step}px)`,
                                    transition: animated ? `transform ${TRANSITION_MS}ms cubic-bezier(0.25,0.46,0.45,0.94)` : 'none',
                                    willChange: 'transform',
                                    backfaceVisibility: 'hidden',
                                    transformStyle: 'preserve-3d'
                                }}
                            >
                                {tripled.map((item: any, i: number) => renderCard(item, i))}
                            </Box>
                        </Box>

                        <Box sx={arrowBtnSx('right', isLightTheme)} onClick={handleNext} role="button" aria-label="Next">
                            <ArrowForwardIcon sx={{fontSize: fontSize[22]}} />
                        </Box>
                    </Box>
                ) : (
                    <Box
                        sx={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            gap: spacing[16],
                            justifyContent: 'center'
                        }}
                    >
                        {awardsList.map((item: any, i: number) => renderCard(item, i))}
                    </Box>
                )}
            </MUIContainer>
        </Box>
    );
};

export default memo(Awards);
